import {
  CheckCircle,
  LayersOutlined,
  MenuBookOutlined,
  ScheduleOutlined,
  School,
  Search,
  WarningAmber,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import {
  LearningAssignmentConflictError,
  ManagerLearningAssignment,
  ManagerLearningCaregiver,
  useLearningAssignments,
} from "../../hooks/useLearningAssignments";
import { borderRadius, color, sessionStatusStyle, typography } from "../../theme/designTokens";
import { ActivityRowHeader } from "../ActivityRowHeader";
import {
  Eyebrow,
  grantedStyle,
  itemCardSx,
  ItemDescription,
  ItemThumbnail,
  ListMessage,
  matchesSearch,
  metaChipSx,
  MetadataChips,
  metaIconSx,
  MetaItem,
  MetaRow,
  panelSx,
} from "./contentCard";
import { LearningAssignmentDialog, LearningAssignmentDialogTarget } from "./LearningAssignmentDialog";

type ItemKind = "course" | "learningPath";
type AssignmentRow = {
  id: string;
  itemKind: ItemKind;
  title: string;
  description: string;
  thumbnailFilename: string | null;
  metric: string;
  duration: string | null;
  tags: string[];
  managerAssignment: ManagerLearningAssignment | null;
  selfAssigned: boolean;
  expired: boolean;
  // False only for courses the organization cannot assign individually
  // because they are delivered through a granted learning path.
  assignable: boolean;
};
type DialogTarget = LearningAssignmentDialogTarget & {
  userId: string;
  itemId: string;
  itemKind: ItemKind;
};

const expiredStyle = sessionStatusStyle.pending;

const assignmentFor = (
  assignments: ManagerLearningAssignment[],
  kind: "MANAGER" | "SELF",
  itemKind: ItemKind,
  itemId: string
) =>
  assignments.find(
    assignment =>
      assignment.kind === kind &&
      (itemKind === "course" ? assignment.courseId === itemId : assignment.learningPathId === itemId)
  );

const initialsOf = (caregiver: ManagerLearningCaregiver): string => {
  const first = caregiver.firstName?.trim().charAt(0) ?? "";
  const last = caregiver.lastName?.trim().charAt(0) ?? "";
  const fallback = caregiver.displayName.trim().charAt(0);
  return (first + last || fallback).toLocaleUpperCase("de-DE");
};

const StatusRow = ({ row }: { row: AssignmentRow }) => {
  const translate = useTranslate();
  if (!row.expired && !row.managerAssignment && !row.selfAssigned) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 0.75 }}>
      {row.expired && (
        <Chip
          icon={<WarningAmber sx={{ fontSize: "15px !important" }} />}
          size="small"
          label={translate("learningAssignments.expired")}
          sx={{
            ...metaChipSx,
            backgroundColor: expiredStyle.bg,
            border: `1px solid ${expiredStyle.border}`,
            color: expiredStyle.color,
            "& .MuiChip-icon": { color: expiredStyle.color },
          }}
        />
      )}
      {row.managerAssignment && (
        <Chip
          icon={<CheckCircle sx={{ fontSize: "15px !important" }} />}
          size="small"
          label={translate("learningAssignments.assigned")}
          sx={{
            ...metaChipSx,
            backgroundColor: color.plannedChipBackground,
            border: `1px solid ${color.plannedChipBorder}`,
            color: color.plannedText,
            "& .MuiChip-icon": { color: color.plannedText },
          }}
        />
      )}
      {row.selfAssigned && (
        <Chip
          size="small"
          variant="outlined"
          label={translate("learningAssignments.selfAssigned")}
          sx={{ height: 22, fontSize: typography.fontSize.xxs }}
        />
      )}
      {row.managerAssignment?.assignedAt && (
        <Typography variant="caption" color="text.secondary">
          {translate("learningAssignments.assignedAt", {
            date: new Date(row.managerAssignment.assignedAt).toLocaleDateString("de-DE"),
          })}
        </Typography>
      )}
    </Box>
  );
};

const RowAction = ({
  row,
  caregiverName,
  userId,
  pending,
  onSelect,
}: {
  row: AssignmentRow;
  caregiverName: string;
  userId: string;
  pending: boolean;
  onSelect: (target: DialogTarget) => void;
}) => {
  const translate = useTranslate();
  const assigned = row.managerAssignment != null;

  if (!assigned && row.expired) return null;
  if (!assigned && !row.assignable) {
    return (
      <Tooltip title={translate("learningAssignments.viaPathHint")}>
        <Chip
          icon={<LayersOutlined sx={{ fontSize: "15px !important" }} />}
          size="small"
          label={translate("learningAssignments.viaPath")}
          sx={{ ...metaChipSx, flex: "0 0 auto" }}
        />
      </Tooltip>
    );
  }

  return (
    <Button
      variant={assigned ? "outlined" : "contained"}
      disabled={pending}
      startIcon={pending ? <CircularProgress size={16} /> : assigned ? <CheckCircle /> : undefined}
      onClick={() =>
        onSelect({
          userId,
          itemId: row.id,
          itemKind: row.itemKind,
          caregiverName,
          contentTitle: row.title,
          assigned,
          expired: row.expired,
        })
      }
      aria-label={translate(
        assigned ? "learningAssignments.actions.unassignLabel" : "learningAssignments.actions.assignLabel",
        { caregiver: caregiverName, title: row.title }
      )}
      sx={{
        flex: "0 0 auto",
        minWidth: 150,
        // Assigned reads as a state, and hovering it hints that clicking removes.
        ...(assigned && {
          backgroundColor: color.white,
          borderColor: grantedStyle.border,
          color: grantedStyle.color,
          "&:hover": { backgroundColor: color.white, borderColor: color.error, color: color.error },
        }),
      }}
    >
      {translate(assigned ? "learningAssignments.actions.unassign" : "learningAssignments.actions.assign")}
    </Button>
  );
};

const AssignmentCards = ({
  rows,
  emptyMessage,
  caregiverName,
  userId,
  isPending,
  onSelect,
}: {
  rows: AssignmentRow[];
  emptyMessage: string;
  caregiverName: string;
  userId: string;
  isPending: (userId: string, itemKind: ItemKind, itemId: string) => boolean;
  onSelect: (target: DialogTarget) => void;
}) => {
  if (rows.length === 0) return <ListMessage message={emptyMessage} />;

  return (
    <>
      {rows.map(row => (
        <Box
          key={`${row.itemKind}-${row.id}`}
          sx={itemCardSx(row.managerAssignment != null)}
          data-testid={`learning-assignment-${row.id}`}
        >
          <ActivityRowHeader
            gap={2}
            leading={<ItemThumbnail thumbnailFilename={row.thumbnailFilename} />}
            title={row.title}
            caption={
              <>
                {row.description && <ItemDescription description={row.description} />}
                <MetaRow>
                  <MetaItem
                    icon={
                      row.itemKind === "course" ? (
                        <MenuBookOutlined sx={metaIconSx} />
                      ) : (
                        <LayersOutlined sx={metaIconSx} />
                      )
                    }
                    label={row.metric}
                  />
                  {row.duration && <MetaItem icon={<ScheduleOutlined sx={metaIconSx} />} label={row.duration} />}
                  <MetadataChips tags={row.tags} />
                </MetaRow>
                <StatusRow row={row} />
              </>
            }
            trailing={
              <RowAction
                row={row}
                caregiverName={caregiverName}
                userId={userId}
                pending={isPending(userId, row.itemKind, row.id)}
                onSelect={onSelect}
              />
            }
          />
        </Box>
      ))}
    </>
  );
};

const SummaryChip = ({
  itemId,
  title,
  itemKind,
  self,
  expired,
}: {
  itemId: string;
  title: string;
  itemKind: ItemKind;
  self: boolean;
  expired: boolean;
}) => {
  const translate = useTranslate();
  const icon =
    itemKind === "course" ? (
      <MenuBookOutlined sx={{ fontSize: "15px !important" }} />
    ) : (
      <LayersOutlined sx={{ fontSize: "15px !important" }} />
    );
  const chip = (
    <Chip
      icon={icon}
      size="small"
      label={title}
      data-testid={`learning-assignment-summary-${itemId}`}
      sx={{
        ...metaChipSx,
        maxWidth: 280,
        ...(expired
          ? {
              backgroundColor: expiredStyle.bg,
              border: `1px solid ${expiredStyle.border}`,
              color: expiredStyle.color,
              "& .MuiChip-icon": { color: expiredStyle.color },
            }
          : !self && {
              backgroundColor: color.plannedChipBackground,
              border: `1px solid ${color.plannedChipBorder}`,
              color: color.plannedText,
              "& .MuiChip-icon": { color: color.plannedText },
            }),
        ...(self && { backgroundColor: color.white }),
      }}
    />
  );
  return self ? <Tooltip title={translate("learningAssignments.selfAssigned")}>{chip}</Tooltip> : chip;
};

const CaregiverRail = ({
  caregivers,
  selectedUserId,
  assignedCountByUserId,
  onSelect,
}: {
  caregivers: ManagerLearningCaregiver[];
  selectedUserId: string;
  assignedCountByUserId: Map<string, number>;
  onSelect: (userId: string) => void;
}) => {
  const translate = useTranslate();
  const [caregiverSearch, setCaregiverSearch] = useState("");
  const filteredCaregivers = caregivers.filter(caregiver => matchesSearch(caregiverSearch, [caregiver.displayName]));

  return (
    <Box sx={{ ...panelSx, width: 264, flex: "0 0 264px", overflow: "hidden" }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: `1px solid ${color.panelBorder}` }}>
        <Eyebrow>{translate("learningAssignments.caregivers")}</Eyebrow>
        <TextField
          size="small"
          fullWidth
          value={caregiverSearch}
          onChange={event => setCaregiverSearch(event.target.value)}
          placeholder={translate("learningAssignments.caregiverSearchPlaceholder")}
          inputProps={{ "aria-label": translate("learningAssignments.caregiverSearchLabel") }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: color.gray }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mt: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: borderRadius.lg,
              backgroundColor: color.background2,
              "& fieldset": { borderColor: color.panelBorder },
            },
          }}
        />
      </Box>
      {filteredCaregivers.length === 0 ? (
        <ListMessage message={translate("learningAssignments.noCaregiverMatch")} />
      ) : (
        <List
          dense
          disablePadding
          aria-label={translate("learningAssignments.caregivers")}
          sx={{ maxHeight: 480, overflowY: "auto" }}
        >
          {filteredCaregivers.map(caregiver => {
            const selected = caregiver.userId === selectedUserId;
            const assignedCount = assignedCountByUserId.get(caregiver.userId) ?? 0;
            return (
              <ListItemButton
                key={caregiver.userId}
                selected={selected}
                onClick={() => onSelect(caregiver.userId)}
                aria-pressed={selected}
                data-testid={`learning-assignment-caregiver-${caregiver.userId}`}
                sx={{
                  gap: 1.25,
                  px: 2,
                  py: 1,
                  "&.Mui-selected": {
                    backgroundColor: color.selectedOptionBackground,
                    "&:hover": { backgroundColor: color.selectedOptionBackground },
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: typography.fontSize.xxs,
                    fontWeight: typography.fontWeight.semibold,
                    backgroundColor: selected ? color.primary : color.secondaryButtonBackground,
                    color: selected ? color.white : color.primary,
                  }}
                >
                  {initialsOf(caregiver)}
                </Avatar>
                <ListItemText
                  primary={caregiver.displayName}
                  primaryTypographyProps={{
                    fontSize: typography.fontSize.xs,
                    fontWeight: selected ? typography.fontWeight.semibold : typography.fontWeight.normal,
                    noWrap: true,
                  }}
                />
                {assignedCount > 0 && (
                  <Chip
                    size="small"
                    label={assignedCount}
                    aria-label={translate("learningAssignments.caregiverAssignments", {
                      smart_count: assignedCount,
                    })}
                    sx={{ ...metaChipSx, minWidth: 28 }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export const LearningAssignmentList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const {
    caregivers,
    courses,
    courseAccessById,
    learningPaths,
    assignments,
    loading,
    error,
    isCaregiverEmpty,
    isCatalogEmpty,
    isAssignmentPending,
    reload,
    setCourseAssignment,
    setLearningPathAssignment,
  } = useLearningAssignments();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [tab, setTab] = useState<ItemKind>("course");
  const [search, setSearch] = useState("");
  const [dialogTarget, setDialogTarget] = useState<DialogTarget | null>(null);

  useEffect(() => {
    if (!caregivers.some(caregiver => caregiver.userId === selectedUserId)) {
      setSelectedUserId(caregivers[0]?.userId ?? "");
    }
  }, [caregivers, selectedUserId]);

  const caregiver = caregivers.find(item => item.userId === selectedUserId) ?? null;
  const selectedAssignments = useMemo(
    () => assignments.filter(assignment => assignment.userId === selectedUserId),
    [assignments, selectedUserId]
  );
  const coursesById = useMemo(() => new Map(courses.map(course => [course.id as string, course])), [courses]);
  const pathsById = useMemo(() => new Map(learningPaths.map(path => [path.id, path])), [learningPaths]);
  const assignedCountByUserId = useMemo(() => {
    const counts = new Map<string, number>();
    assignments.forEach(assignment => {
      if (assignment.kind !== "MANAGER") return;
      counts.set(assignment.userId, (counts.get(assignment.userId) ?? 0) + 1);
    });
    return counts;
  }, [assignments]);

  const rows = useMemo(() => {
    const currentIds = new Set(tab === "course" ? courses.map(item => item.id) : learningPaths.map(item => item.id));
    const toRow = (
      item: {
        id: string;
        title: string;
        description: string;
        thumbnailFilename: string;
        estimatedDurationMinutes: number;
        tags: string[];
      },
      metric: string,
      assignable: boolean
    ): AssignmentRow => ({
      id: item.id,
      itemKind: tab,
      title: item.title,
      description: item.description,
      thumbnailFilename: item.thumbnailFilename,
      metric,
      duration: translate("learningAssignments.values.duration", { minutes: item.estimatedDurationMinutes }),
      tags: item.tags,
      managerAssignment: assignmentFor(selectedAssignments, "MANAGER", tab, item.id) ?? null,
      selfAssigned: assignmentFor(selectedAssignments, "SELF", tab, item.id) != null,
      expired: false,
      assignable,
    });
    const currentRows =
      tab === "course"
        ? courses.map(item =>
            toRow(
              item,
              translate("learningAssignments.values.lessons", { smart_count: item.lessonCount }),
              // Without an access entry (older backend), stay permissive: the
              // backend still validates the grant on assignment.
              courseAccessById.get(item.id)?.directAccess ?? true
            )
          )
        : learningPaths.map(item =>
            toRow(item, translate("learningAssignments.values.courses", { smart_count: item.courses.length }), true)
          );
    const expiredIds = Array.from(
      new Set(
        selectedAssignments
          .filter(assignment => assignment.itemKind === (tab === "course" ? "COURSE" : "LEARNING_PATH"))
          .map(assignment => (tab === "course" ? assignment.courseId : assignment.learningPathId))
          .filter((id): id is string => id != null && !currentIds.has(id))
      )
    );
    return [
      ...currentRows,
      ...expiredIds.map(id => ({
        id,
        itemKind: tab,
        title: translate(tab === "course" ? "learningAssignments.expiredCourse" : "learningAssignments.expiredPath"),
        description: "",
        thumbnailFilename: null,
        metric: translate("learningAssignments.expiredMetric"),
        duration: null,
        tags: [],
        managerAssignment: assignmentFor(selectedAssignments, "MANAGER", tab, id) ?? null,
        selfAssigned: assignmentFor(selectedAssignments, "SELF", tab, id) != null,
        expired: true,
        assignable: true,
      })),
    ].filter(row => matchesSearch(search, [row.title, row.description, ...row.tags]));
  }, [courseAccessById, courses, learningPaths, search, selectedAssignments, tab, translate]);

  const summaryItems = useMemo(
    () =>
      selectedAssignments.map(assignment => {
        const isCourse = assignment.itemKind === "COURSE";
        const itemId = (isCourse ? assignment.courseId : assignment.learningPathId) ?? assignment.id;
        const catalogTitle = isCourse
          ? coursesById.get(assignment.courseId ?? "")?.title
          : pathsById.get(assignment.learningPathId ?? "")?.title;
        return {
          key: `${assignment.itemKind}-${itemId}-${assignment.kind}`,
          itemId,
          itemKind: (isCourse ? "course" : "learningPath") as ItemKind,
          title:
            catalogTitle ??
            translate(isCourse ? "learningAssignments.expiredCourse" : "learningAssignments.expiredPath"),
          self: assignment.kind === "SELF",
          expired: catalogTitle == null,
        };
      }),
    [coursesById, pathsById, selectedAssignments, translate]
  );

  const confirmChange = async () => {
    if (!dialogTarget) return;
    try {
      const nextAssigned = !dialogTarget.assigned;
      if (dialogTarget.itemKind === "course") {
        await setCourseAssignment(dialogTarget.userId, dialogTarget.itemId, nextAssigned);
      } else {
        await setLearningPathAssignment(dialogTarget.userId, dialogTarget.itemId, nextAssigned);
      }
      notify(
        translate(
          nextAssigned ? "learningAssignments.notifications.assigned" : "learningAssignments.notifications.unassigned",
          { caregiver: dialogTarget.caregiverName, title: dialogTarget.contentTitle }
        ),
        { type: "success" }
      );
      setDialogTarget(null);
    } catch (updateError) {
      notify(
        translate(
          updateError instanceof LearningAssignmentConflictError
            ? "learningAssignments.notifications.conflict"
            : updateError instanceof Error && /not granted/i.test(updateError.message)
              ? "learningAssignments.notifications.notGranted"
              : "learningAssignments.notifications.error"
        ),
        { type: "error" }
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: 160 }}>
        <CircularProgress aria-label={translate("learningAssignments.loading")} />
      </Box>
    );
  }
  if (isCaregiverEmpty) {
    return (
      <Box sx={{ ...panelSx, px: 3, py: 5, textAlign: "center" }}>
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: 56,
            height: 56,
            mx: "auto",
            mb: 1.5,
            borderRadius: "50%",
            backgroundColor: color.background2,
          }}
        >
          <School sx={{ fontSize: 28, color: color.iconMuted }} />
        </Box>
        <Typography variant="h6">{translate("learningAssignments.noCaregiversTitle")}</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, mx: "auto", maxWidth: 460 }}>
          {translate("learningAssignments.noCaregiversDescription")}
        </Typography>
      </Box>
    );
  }

  const emptyMessage = search.trim()
    ? translate("learningAssignments.noSearchResults")
    : translate("learningAssignments.noContent");

  return (
    <Box data-testid="learning-assignment-list">
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: borderRadius.sm }}
          action={
            <Button color="inherit" onClick={() => void reload()}>
              {translate("learningAssignments.actions.retry")}
            </Button>
          }
        >
          {translate("learningAssignments.loadError")}
        </Alert>
      )}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <CaregiverRail
          caregivers={caregivers}
          selectedUserId={selectedUserId}
          assignedCountByUserId={assignedCountByUserId}
          onSelect={userId => {
            setSelectedUserId(userId);
            setDialogTarget(null);
          }}
        />

        <Box sx={{ flex: 1, minWidth: 400 }}>
          {caregiver && (
            <Box
              sx={{
                ...panelSx,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                px: 2.5,
                py: 1.75,
                mb: 2,
              }}
            >
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  backgroundColor: color.secondaryButtonBackground,
                  color: color.primary,
                }}
              >
                {initialsOf(caregiver)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Eyebrow>{translate("learningAssignments.summaryLabel")}</Eyebrow>
                <Typography variant="h6" sx={{ lineHeight: typography.lineHeight.xs }}>
                  {caregiver.displayName}
                </Typography>
                {summaryItems.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: typography.fontSize.xs, mt: 0.5 }}
                    data-testid="learning-assignments-empty"
                  >
                    {translate("learningAssignments.empty", { caregiver: caregiver.displayName })}
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                    {summaryItems.map(item => (
                      <SummaryChip
                        key={item.key}
                        itemId={item.itemId}
                        title={item.title}
                        itemKind={item.itemKind}
                        self={item.self}
                        expired={item.expired}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {isCatalogEmpty && rows.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: borderRadius.sm }}>
              {translate("learningAssignments.noCatalog")}
            </Alert>
          ) : (
            <Box sx={{ ...panelSx, overflow: "hidden" }}>
              <Box
                sx={{
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  borderBottom: `1px solid ${color.panelBorder}`,
                }}
              >
                <Tabs
                  value={tab}
                  onChange={(_event, value) => setTab(value)}
                  aria-label={translate("learningAssignments.tabs.label")}
                  sx={{
                    minHeight: 48,
                    "& .MuiTab-root": {
                      minHeight: 48,
                      px: 1.5,
                      textTransform: "none",
                      fontSize: typography.fontSize.md,
                      fontWeight: typography.fontWeight.medium,
                    },
                  }}
                >
                  <Tab value="course" label={translate("learningAssignments.tabs.courses")} />
                  <Tab value="learningPath" label={translate("learningAssignments.tabs.paths")} />
                </Tabs>
                <TextField
                  size="small"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder={translate("learningAssignments.searchPlaceholder")}
                  inputProps={{ "aria-label": translate("learningAssignments.searchLabel") }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 20, color: color.gray }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    my: 1,
                    minWidth: 240,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: borderRadius.lg,
                      backgroundColor: color.background2,
                      "& fieldset": { borderColor: color.panelBorder },
                    },
                  }}
                />
              </Box>

              <Box
                sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25, backgroundColor: color.background2 }}
              >
                {caregiver && (
                  <AssignmentCards
                    rows={rows}
                    emptyMessage={emptyMessage}
                    caregiverName={caregiver.displayName}
                    userId={caregiver.userId}
                    isPending={isAssignmentPending}
                    onSelect={setDialogTarget}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      <LearningAssignmentDialog
        target={dialogTarget}
        loading={
          dialogTarget ? isAssignmentPending(dialogTarget.userId, dialogTarget.itemKind, dialogTarget.itemId) : false
        }
        onConfirm={() => void confirmChange()}
        onClose={() => setDialogTarget(null)}
      />
    </Box>
  );
};
